/**
 * OOXML parser for Word DOCX comments + track changes (w:ins / w:del).
 * Adapted from contract-ai extract-docx-annotations (ideas only — no package coupling).
 */

import JSZip from "jszip";
import type {
  DocxAnnotations,
  DocxAnnotationsPayload,
  ExtractedDocxComment,
  ExtractedDocxRevision,
} from "../types.js";

const W_NS = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";

function localName(tag: string): string {
  const i = tag.indexOf(":");
  return i >= 0 ? tag.slice(i + 1) : tag;
}

function decodeXmlEntities(s: string): string {
  return s
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&");
}

function attr(tag: string, name: string): string | undefined {
  const re = new RegExp(
    `(?:\\s|:)${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`,
    "i"
  );
  const m = tag.match(re);
  return m?.[1] ?? m?.[2];
}

function wAttr(openTag: string, local: string): string | undefined {
  return (
    attr(openTag, `w:${local}`) ??
    attr(openTag, local) ??
    attr(openTag, `{${W_NS}}${local}`)
  );
}

type OpenTag = { raw: string; name: string; selfClosing: boolean };
type Token =
  | { kind: "open"; tag: OpenTag }
  | { kind: "close"; name: string }
  | { kind: "text"; value: string };

function tokenizeXml(xml: string): Token[] {
  const tokens: Token[] = [];
  const re = /<\/([^>]+)>|<([^>]+?)\s*\/>|<([^>]+?)>|([^<]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml)) !== null) {
    if (m[1] != null) {
      tokens.push({ kind: "close", name: localName(m[1].trim()) });
    } else if (m[2] != null) {
      const raw = m[2].trim();
      if (raw.startsWith("?") || raw.startsWith("!")) continue;
      const name = raw.split(/\s+/)[0] ?? raw;
      tokens.push({
        kind: "open",
        tag: { raw, name: localName(name), selfClosing: true },
      });
    } else if (m[3] != null) {
      const raw = m[3].trim();
      if (raw.startsWith("?") || raw.startsWith("!")) continue;
      const name = raw.split(/\s+/)[0] ?? raw;
      tokens.push({
        kind: "open",
        tag: { raw, name: localName(name), selfClosing: false },
      });
    } else if (m[4] != null) {
      tokens.push({ kind: "text", value: m[4] });
    }
  }
  return tokens;
}

function extractTextFromCommentXml(xml: string): Map<
  string,
  { author: string; date?: string; body: string }
> {
  const map = new Map<string, { author: string; date?: string; body: string }>();
  const tokens = tokenizeXml(xml);
  let currentId: string | null = null;
  let author = "";
  let date: string | undefined;
  let bodyParts: string[] = [];
  let inText = false;
  let inDelText = false;

  for (const tok of tokens) {
    if (tok.kind === "open") {
      if (tok.tag.name === "comment") {
        currentId = wAttr(tok.tag.raw, "id") ?? null;
        author = wAttr(tok.tag.raw, "author") ?? "Word";
        date = wAttr(tok.tag.raw, "date");
        bodyParts = [];
      } else if (tok.tag.name === "t") {
        inText = true;
      } else if (tok.tag.name === "delText") {
        inDelText = true;
      } else if (
        currentId != null &&
        (tok.tag.name === "tab" ||
          tok.tag.name === "br" ||
          tok.tag.name === "cr")
      ) {
        bodyParts.push(tok.tag.name === "tab" ? "\t" : "\n");
      }
    } else if (tok.kind === "close") {
      if (tok.name === "comment" && currentId != null) {
        map.set(currentId, {
          author: author || "Word",
          date,
          body: bodyParts.join("").replace(/\s+/g, " ").trim(),
        });
        currentId = null;
      } else if (tok.name === "t") {
        inText = false;
      } else if (tok.name === "delText") {
        inDelText = false;
      } else if (tok.name === "p" && currentId != null && bodyParts.length) {
        bodyParts.push("\n");
      }
    } else if (
      tok.kind === "text" &&
      currentId != null &&
      (inText || inDelText)
    ) {
      bodyParts.push(decodeXmlEntities(tok.value));
    }
  }
  return map;
}

type RevFrame = {
  type: "ins" | "del";
  id: string;
  author: string;
  date?: string;
  text: string[];
};

export function parseDocumentXml(
  documentXml: string,
  commentsById: Map<string, { author: string; date?: string; body: string }>
): DocxAnnotations {
  const tokens = tokenizeXml(documentXml);
  const finalParts: string[] = [];
  const originalParts: string[] = [];
  const revisions: ExtractedDocxRevision[] = [];
  const quoteById = new Map<string, string[]>();
  const activeQuoteIds = new Set<string>();
  const revStack: RevFrame[] = [];

  const appendText = (
    text: string,
    opts?: { onlyOriginal?: boolean; onlyFinal?: boolean }
  ) => {
    if (!text) return;
    if (!opts?.onlyOriginal) finalParts.push(text);
    if (!opts?.onlyFinal) originalParts.push(text);
    for (const id of activeQuoteIds) {
      const arr = quoteById.get(id);
      if (arr) arr.push(text);
    }
  };

  const inIns = () => revStack.some((r) => r.type === "ins");
  const inDel = () => revStack.some((r) => r.type === "del");

  let inT = false;
  let inDelText = false;
  let skipDrawingDepth = 0;

  for (const tok of tokens) {
    if (tok.kind === "open") {
      const n = tok.tag.name;

      if (n === "drawing" || n === "pict" || n === "object") {
        if (!tok.tag.selfClosing) skipDrawingDepth += 1;
        continue;
      }
      if (skipDrawingDepth > 0) continue;

      if (n === "commentRangeStart") {
        const id = wAttr(tok.tag.raw, "id");
        if (id != null) {
          activeQuoteIds.add(id);
          if (!quoteById.has(id)) quoteById.set(id, []);
        }
      } else if (n === "commentRangeEnd") {
        const id = wAttr(tok.tag.raw, "id");
        if (id != null) activeQuoteIds.delete(id);
      } else if (n === "ins" || n === "moveTo") {
        const id = wAttr(tok.tag.raw, "id") ?? crypto.randomUUID();
        const author = wAttr(tok.tag.raw, "author") ?? "Word";
        const date = wAttr(tok.tag.raw, "date");
        if (!tok.tag.selfClosing) {
          revStack.push({ type: "ins", id, author, date, text: [] });
        }
      } else if (n === "del" || n === "moveFrom") {
        const id = wAttr(tok.tag.raw, "id") ?? crypto.randomUUID();
        const author = wAttr(tok.tag.raw, "author") ?? "Word";
        const date = wAttr(tok.tag.raw, "date");
        if (!tok.tag.selfClosing) {
          revStack.push({ type: "del", id, author, date, text: [] });
        }
      } else if (n === "t") {
        inT = true;
      } else if (n === "delText") {
        inDelText = true;
      } else if (n === "tab") {
        const ch = "\t";
        if (inDel()) {
          appendText(ch, { onlyOriginal: true });
          const top = revStack[revStack.length - 1];
          if (top?.type === "del") top.text.push(ch);
        } else if (inIns()) {
          appendText(ch, { onlyFinal: true });
          const top = revStack[revStack.length - 1];
          if (top?.type === "ins") top.text.push(ch);
        } else {
          appendText(ch);
        }
      } else if (n === "br" || n === "cr") {
        const ch = "\n";
        if (inDel()) {
          appendText(ch, { onlyOriginal: true });
          const top = revStack[revStack.length - 1];
          if (top?.type === "del") top.text.push(ch);
        } else if (inIns()) {
          appendText(ch, { onlyFinal: true });
          const top = revStack[revStack.length - 1];
          if (top?.type === "ins") top.text.push(ch);
        } else {
          appendText(ch);
        }
      }
    } else if (tok.kind === "close") {
      const n = tok.name;
      if (n === "drawing" || n === "pict" || n === "object") {
        if (skipDrawingDepth > 0) skipDrawingDepth -= 1;
        continue;
      }
      if (skipDrawingDepth > 0) continue;

      if (n === "t") {
        inT = false;
      } else if (n === "delText") {
        inDelText = false;
      } else if (
        n === "ins" ||
        n === "moveTo" ||
        n === "del" ||
        n === "moveFrom"
      ) {
        const frame = revStack.pop();
        if (frame) {
          const text = frame.text.join("");
          if (text.trim()) {
            revisions.push({
              word_id: frame.id,
              type: frame.type,
              author: frame.author,
              date: frame.date,
              text,
            });
          }
        }
      } else if (n === "p") {
        if (!inIns() && !inDel()) {
          appendText("\n");
        } else if (inIns() && !inDel()) {
          appendText("\n", { onlyFinal: true });
        } else if (inDel() && !inIns()) {
          appendText("\n", { onlyOriginal: true });
        }
      }
    } else if (tok.kind === "text" && skipDrawingDepth === 0) {
      if (inT) {
        const text = decodeXmlEntities(tok.value);
        if (inDel()) {
          appendText(text, { onlyOriginal: true });
          const top = revStack[revStack.length - 1];
          if (top?.type === "del") top.text.push(text);
        } else if (inIns()) {
          appendText(text, { onlyFinal: true });
          const top = revStack[revStack.length - 1];
          if (top?.type === "ins") top.text.push(text);
        } else {
          appendText(text);
        }
      } else if (inDelText) {
        const text = decodeXmlEntities(tok.value);
        appendText(text, { onlyOriginal: true });
        const top = revStack[revStack.length - 1];
        if (top?.type === "del") top.text.push(text);
      }
    }
  }

  const normalizeBody = (s: string) =>
    s
      .replace(/\r\n/g, "\n")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

  const comments: ExtractedDocxComment[] = [];
  const finalJoined = finalParts.join("");
  for (const [id, meta] of commentsById) {
    const quote = (quoteById.get(id) ?? []).join("").replace(/\s+/g, " ").trim();
    let context: string | undefined;
    if (quote) {
      const idx = finalJoined.indexOf(quote);
      if (idx >= 0) {
        const start = Math.max(0, idx - 60);
        const end = Math.min(finalJoined.length, idx + quote.length + 60);
        context = finalJoined.slice(start, end).replace(/\s+/g, " ").trim();
      }
    }
    comments.push({
      word_id: id,
      author: meta.author,
      date: meta.date,
      body: meta.body,
      quote,
      context,
    });
  }

  const insertions = revisions.filter((r) => r.type === "ins").length;
  const deletions = revisions.filter((r) => r.type === "del").length;

  return {
    comments,
    revisions,
    finalText: normalizeBody(finalParts.join("")),
    originalText: normalizeBody(originalParts.join("")),
    counts: {
      comments: comments.length,
      insertions,
      deletions,
    },
  };
}

export async function extractDocxAnnotations(
  buffer: ArrayBuffer | Uint8Array
): Promise<DocxAnnotations> {
  const zip = await JSZip.loadAsync(buffer);
  const documentFile = zip.file("word/document.xml");
  if (!documentFile) {
    throw new Error("DOCX فاقد word/document.xml است");
  }
  const documentXml = await documentFile.async("string");

  let commentsById = new Map<
    string,
    { author: string; date?: string; body: string }
  >();
  const commentsFile = zip.file("word/comments.xml");
  if (commentsFile) {
    const commentsXml = await commentsFile.async("string");
    commentsById = extractTextFromCommentXml(commentsXml);
  }

  return parseDocumentXml(documentXml, commentsById);
}

export function toDocxAnnotationsPayload(
  ann: DocxAnnotations
): DocxAnnotationsPayload {
  return {
    comments: ann.comments,
    revisions: ann.revisions,
    finalText: ann.finalText,
    originalText: ann.originalText,
  };
}

export { tokenizeXml, wAttr, decodeXmlEntities };
